// Option lists and colours shared by the hazard-log pages. Mirrors the enums
// in server/src/models/AdminCRM/CRMP/HazardLog.js - keep the two in step.

export const REGISTERS = {
  Clinical: {
    key: 'Clinical',
    label: 'Clinical Hazard Log',
    short: 'Clinical',
    path: '/clinical-risk',
    color: '#0F6670',
    subtitle:
      'Hazards about AmbuNet as a product and organisation: clinical content and algorithms, third-party services, infrastructure, data protection, regulatory and process. This is the DCB0129 register the safety case rests on.',
  },
  Codebase: {
    key: 'Codebase',
    label: 'Codebase Hazard Log',
    short: 'Codebase',
    path: '/clinical-risk/codebase',
    color: '#5B48B0',
    subtitle:
      'Hazards found by reviewing the software itself. Each cites file and line evidence and is reconciled against the code after releases.',
  },
}

export const REGISTER_OPTIONS = Object.keys(REGISTERS)

export const MODULE_OPTIONS = [
  'Events',
  'Clinical Governance',
  'Patient Care Records',
  'Fleet Management',
  'Personnel',
  'Patient Transport',
  'Platform & Security',
  'Clinical Content & Algorithms',
  'Third-Party Services',
  'Infrastructure & Operations',
  'Organisation & Governance',
]

export const HAZARD_TYPE_OPTIONS = [
  'Clinical Safety',
  'Information Security',
  'Data Protection',
  'Operational',
  'Regulatory/Compliance',
]

export const SOURCE_OPTIONS = [
  'Incident Report',
  'Risk Assessment',
  'Audit',
  'Testing/QA',
  'User Feedback',
  'Code Review',
  'Penetration Test',
  'Clinical Governance Review',
  'Regulatory/Inspection',
  'Other',
]

export const STATUS_OPTIONS = ['Open', 'In Progress', 'Mitigated', 'Accepted']
export const RISK_ACCEPTABILITY_OPTIONS = ['Acceptable', 'ALARP', 'Unacceptable']
export const LIKELIHOOD_OPTIONS = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain']
export const SEVERITY_OPTIONS = ['Negligible', 'Minor', 'Moderate', 'Major', 'Catastrophic']
export const RISK_RATING_OPTIONS = ['Low', 'Medium', 'High', 'Critical']

export const STATUS_COLOR = {
  Open: '#B42318',
  'In Progress': '#B54708',
  Mitigated: '#27803B',
  Accepted: '#268EBB',
}

export const RISK_COLOR = {
  Low: '#27803B',
  Medium: '#B54708',
  High: '#B42318',
  Critical: '#7A271A',
}

export const MODULE_COLOR = {
  Events: '#268EBB',
  'Clinical Governance': '#B54708',
  'Patient Care Records': '#85276A',
  'Fleet Management': '#27803B',
  Personnel: '#0F6670',
  'Patient Transport': '#557A95',
  'Platform & Security': '#B42318',
  'Clinical Content & Algorithms': '#7A271A',
  'Third-Party Services': '#6B4FBB',
  'Infrastructure & Operations': '#3E6A8C',
  'Organisation & Governance': '#64748B',
}

export const HAZARD_TYPE_COLOR = {
  'Clinical Safety': '#B42318',
  'Information Security': '#5B48B0',
  'Data Protection': '#268EBB',
  Operational: '#B54708',
  'Regulatory/Compliance': '#557A95',
}

export const riskTone = (rating) => RISK_COLOR[rating] || '#64748B'

// CRMP Appendix A matrix: likelihood row x severity column gives a score of
// 1-4, which maps to the rating. Mirrors riskRatingFor on the server; the
// server recomputes on save, so this is for display only.
export const RISK_MATRIX = {
  'Almost Certain': [2, 3, 3, 4, 4],
  Likely: [1, 2, 3, 4, 4],
  Possible: [1, 2, 2, 3, 3],
  Unlikely: [1, 1, 2, 2, 3],
  Rare: [1, 1, 1, 1, 2],
}
export const RISK_RATINGS = { 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Critical' }

export const riskScoreFor = (likelihood, severity) => {
  const row = RISK_MATRIX[likelihood]
  const col = SEVERITY_OPTIONS.indexOf(severity)
  return row && col !== -1 ? row[col] : null
}

export const riskRatingFor = (likelihood, severity) =>
  RISK_RATINGS[riskScoreFor(likelihood, severity)] || ''

// "High (3)" for a fully scored risk, otherwise a prompt.
export const riskRatingLabel = (risk = {}) => {
  const score = riskScoreFor(risk?.likelihood, risk?.severity)
  return score ? `${RISK_RATINGS[score]} (${score})` : ''
}

export const isHighRisk = (rating = '') =>
  ['high', 'critical'].includes(String(rating).toLowerCase())

// A hazard still has work outstanding when any planned control is not yet
// implemented.
export const outstandingControls = (hazard) =>
  (hazard?.controlMeasures || []).filter((c) => c?.type === 'planned' && !c?.implemented).length

export const implementedControls = (hazard) =>
  (hazard?.controlMeasures || []).filter((c) => c?.implemented).length

// DCB0160 support pack. Mirrors CUSTOMER_CONTROL_TYPES on the model.
export const CUSTOMER_CONTROL_TYPES = [
  'Configuration',
  'Training',
  'Procedure',
  'Business continuity',
  'Awareness only',
]

export const CUSTOMER_CONTROL_HINT = {
  Configuration: 'A setting the organisation must turn on or check',
  Training: 'Something staff must be taught before or while using the feature',
  Procedure: 'A local process the organisation must have in place',
  'Business continuity': 'What to do when the feature or platform is unavailable',
  'Awareness only': 'No action beyond knowing the limitation exists',
}

export const inCustomerPack = (hazard) => !!hazard?.customerFacing?.include
